import type { ListResponse, ProfileCategoryCountsResponse, ProfileResponse } from '@shared/schemas';

import type { FilterGroup } from '@/types/filters';

export type LoadStatus = 'idle' | 'loading' | 'loaded' | 'failed';

/** Rows per page, as on the HNWIs page. */
export const DEFAULT_PAGE_SIZE = 25;

/**
 * The last "Ask AI" request. The answer's rules are not applied here: the rule
 * tree is the page's local edit state, so the page picks `answer` up and loads
 * it. `answerId` changes with every answer, so asking twice applies twice.
 */
export interface AiSearchState {
  status: 'idle' | 'asking' | 'failed';
  error: string | null;
  answer: { filters: FilterGroup; interpretation: string; query: string } | null;
  answerId: number;
}

export interface FacetOptions {
  countries: string[] | null;
  tags: string[] | null;
}

/**
 * Redux state for the client Discovered LP Detail page (#689).
 *
 * Two lifecycles: the list itself (`list`, `commentCount`, `loadStatus`; a
 * failure before any list arrived sends the page back to the listing) and its
 * profiles, searched on the server a page at a time like the HNWIs page
 * (`rows` / `total` / `page` / `filters` / `search`). The facet menus' sources
 * (`categoryCounts`, `facetOptions`) describe the list, not the search, so
 * they do not move when a filter is added. Filter-bar edit state, column
 * config, selection and drawers stay local to the page.
 */
export interface DiscoveredLpDetailState {
  /** The list the profiles are read from; no page is fetched before it is set. */
  listId: string | null;
  list: ListResponse | null;
  commentCount: number;
  loadStatus: LoadStatus;
  error: string | null;

  rows: ProfileResponse[];
  total: number;
  rowsLoading: boolean;
  rowsError: string | null;
  page: number;
  pageSize: number;
  filters: FilterGroup | null;
  search: string;
  aiSearch: AiSearchState;
  categoryCounts: ProfileCategoryCountsResponse | null;
  facetOptions: FacetOptions;
}

export const initialState: DiscoveredLpDetailState = {
  listId: null,
  list: null,
  commentCount: 0,
  loadStatus: 'idle',
  error: null,

  rows: [],
  total: 0,
  rowsLoading: false,
  rowsError: null,
  page: 0,
  pageSize: DEFAULT_PAGE_SIZE,
  filters: null,
  search: '',
  aiSearch: { status: 'idle', error: null, answer: null, answerId: 0 },
  categoryCounts: null,
  facetOptions: { countries: null, tags: null },
};
